import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useContext, useEffect, useState, useRef } from "react";
import { FileIcon as LucideFileIcon, Clock, Play, Download } from "lucide-react";
import * as cadviewer from "cadviewer";
import useConfig from "../../demos/hooks/useConfig";
import { CadviewerContext } from "../../context/Cadviewer.Context";

export interface VersionInfo {
  name: string;
  date: number;
  size: number;
  path: string;
  filename: string;
  isCurrent?: boolean;
  isInitial?: boolean;
}

interface VersionHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  filePath: string;
  fileName: string;
  currentDrawingPath?: string;
  currentDrawingVersion?: string;
  onLoadVersion: (path: string, name: string, modified: string, versionName: string) => void;
  onViewVersion?: (path: string, name: string) => void;
  onEditVersion?: (path: string, name: string) => void;
}

export const VersionHistoryModal = ({
  isOpen,
  onClose,
  filePath,
  fileName,
  currentDrawingPath,
  currentDrawingVersion,
  onLoadVersion,
  onViewVersion,
  onEditVersion,
}: VersionHistoryModalProps) => {
  const { config } = useConfig();
  const { ServerBackEndUrl } = config;
  const { user, setCurrentDrawingVersion } = useContext(CadviewerContext);
  
  const [loading, setLoading] = useState(false);
  const [versions, setVersions] = useState<VersionInfo[]>([]);
  const [currentVersion, setCurrentVersion] = useState<VersionInfo | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [pendingUploadFile, setPendingUploadFile] = useState<File | null>(null);
  const [uploadNote, setUploadNote] = useState("New Version");

  const handleFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setPendingUploadFile(file);
    setUploadNote("v" + Math.max(2, versions.length + 1));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const executeUpload = async () => {
    if (!pendingUploadFile) return;

    try {
      setLoading(true);
      const folderPath = filePath.substring(0, Math.max(filePath.lastIndexOf("\\"), filePath.lastIndexOf("/")));
      
      const formdata = new FormData();
      formdata.append("global", "true");
      formdata.append("path", folderPath);
      formdata.append("targetFilename", fileName);
      
      const vData: Record<string, string> = {};
      vData[fileName] = uploadNote || "New Version";
      formdata.append("versionData", JSON.stringify(vData));
      formdata.append("files", pendingUploadFile, pendingUploadFile.name);

      const headers = new Headers();
      if (user?.token) {
        headers.append("Authorization", `Bearer ${user.token}`);
      }

      let endpoint = "settings/folders/upload";
      if (!ServerBackEndUrl.endsWith("/")) endpoint = "/" + endpoint;

      const response = await fetch(`${ServerBackEndUrl}${endpoint}`, {
        method: "POST",
        body: formdata,
        headers,
      });

      if (response.ok) {
        setPendingUploadFile(null);
        // Refresh versions
        fetchVersions();
      } else {
        const text = await response.text();
        console.error("Upload failed:", text);
        alert("Upload failed: " + text);
      }
    } catch (err) {
      console.error("Upload error:", err);
      alert("Error uploading file.");
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && filePath) {
      fetchVersions();
    }
  }, [isOpen, filePath]);

  const fetchVersions = async () => {
    try {
      setLoading(true);
      let headers: any = undefined;
      if (config.postFixServerToken) {
        headers = { "Content-Type": "application/json" };
      }
      if (config.bearerToken) {
        headers = { ...headers, Authorization: `${config.bearerToken}` };
      }
      if (user?.token) {
        headers = { ...headers, Authorization: `Bearer ${user.token}` };
      }

      // Build endpoint URL
      let endpoint = "settings/folders/versions";
      if (!ServerBackEndUrl.endsWith("/")) endpoint = "/" + endpoint;
      
      const response = await fetch(`${ServerBackEndUrl}${endpoint}?path=${encodeURIComponent(filePath)}`, {
        method: "GET",
        headers,
      });

      if (response.ok) {
        const data = await response.json();
        setCurrentVersion(data.current);
        setVersions(data.versions || []);
      } else {
        console.error("Failed to fetch versions:", await response.text());
      }
    } catch (err) {
      console.error("Error fetching versions:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (fileName.toLowerCase().endsWith(".json") || fileName.toLowerCase().endsWith(".svg")) return;

    if (versions.length > 0 && currentDrawingVersion === "LATEST_FROM_TREE") {
      // We loaded the file from the left panel. The version we loaded was whatever the newest version was at that time.
      // We lock the currentDrawingVersion to this specific version's name so it doesn't move when new uploads happen.
      setCurrentDrawingVersion(versions[0].name || "Initial");
    } else if (versions.length === 0 && currentVersion && currentDrawingVersion === "LATEST_FROM_TREE") {
      setCurrentDrawingVersion("Initial");
    }
  }, [versions, currentVersion, currentDrawingVersion, fileName]);

  const formatSize = (bytes: number) => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // The server now returns server-relative URL paths (e.g. /content/versions/...)
  // instead of absolute disk paths. Convert them to a fully qualified URL so
  // CADViewer receives a standard HTTP URL with no drive-letter or backslash issues.
  // Each path segment is percent-encoded so spaces and special chars survive the HTTP
  // request. directload2.js decodes with decodeURI + manual replacements, which
  // correctly handles %20→space. Commas, apostrophes and parentheses are left
  // unencoded because decodeURI does NOT decode %2C / %27 / %28 / %29.
  const toAbsUrl = (relOrAbsPath: string): string => {
    if (!relOrAbsPath) return relOrAbsPath;
    // Already a full URL (http/https) – return as-is
    if (/^https?:\/\//i.test(relOrAbsPath)) return relOrAbsPath;
    const base = ServerBackEndUrl.endsWith('/') ? ServerBackEndUrl.slice(0, -1) : ServerBackEndUrl;
    const rel = relOrAbsPath.startsWith('/') ? relOrAbsPath : '/' + relOrAbsPath;
    // Encode each segment individually, then restore chars that decodeURI handles
    const encodedRel = rel.split('/').map((seg, i) =>
      i === 0 && seg === '' ? '' : encodeURIComponent(seg)
        .replace(/%2C/g, ',')   // comma – decodeURI won't decode %2C
        .replace(/%27/g, "'")   // apostrophe
        .replace(/%28/g, '(')   // parens
        .replace(/%29/g, ')')
    ).join('/');
    return base + encodedRel;
  };


  const renderVersionRow = (v: VersionInfo, isCurrentRow: boolean, currentInfo: VersionInfo | null) => {
    const rowVersionName = v.name || "Initial";

    let isLoadedCurrent = false;

    if (fileName.toLowerCase().endsWith(".json") || fileName.toLowerCase().endsWith(".svg")) {
      isLoadedCurrent = isCurrentRow;
    } else {
      if (currentDrawingVersion && currentDrawingVersion !== "LATEST_FROM_TREE") {
        isLoadedCurrent = (rowVersionName === currentDrawingVersion);
      } else {
        isLoadedCurrent = currentDrawingPath ? (v.path === currentDrawingPath || (isCurrentRow && currentInfo?.path === currentDrawingPath)) : false;
      }
    }
    
    return (
    <tr key={v.path} className={`hover:bg-gray-50 transition-colors border-b border-gray-100 ${isLoadedCurrent ? "bg-green-50/30" : ""}`}>
      <td className="py-3 px-4">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">
            {v.name === "Current" ? "Latest" : (v.name || "Initial")}
          </span>
          {isLoadedCurrent && (
            <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
              Current
            </span>
          )}
          {v.isInitial && !isLoadedCurrent && (
            <span className="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-[10px] font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10">
              Initial
            </span>
          )}
        </div>
      </td>
      <td className="py-3 px-4 text-sm text-gray-600">
        {new Date(v.date).toLocaleString('en-CA', { hour12: false }).replace(',', '')}
      </td>
      <td className="py-3 px-4 text-sm text-gray-500">
        {formatSize(v.size)}
      </td>
      <td className="py-3 px-4 text-right">
        {fileName.toLowerCase().endsWith(".json") ? (
          <div className="flex justify-end gap-2">
            <button
              onClick={() => {
                onViewVersion?.(v.path, fileName);
              }}
              className="inline-flex items-center justify-center gap-1 rounded-md bg-white px-3 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            >
              View
            </button>
            <button
              onClick={() => {
                onEditVersion?.(v.path, fileName);
              }}
              className="inline-flex items-center justify-center gap-1 rounded-md bg-white px-3 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            >
              Edit
            </button>
          </div>
        ) : (
          <button
            onClick={() => {
              const dateStr = new Date(v.date).toISOString();
              const pathToLoad = toAbsUrl(v.path);
              const loadName = v.name || "Initial";
              onLoadVersion(pathToLoad, fileName, dateStr, loadName);
              onClose();
            }}
            className="inline-flex items-center justify-center gap-1 rounded-md bg-white px-3 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          >
            <Play size={14} className="text-primary-600" />
            Load
          </button>
        )}
      </td>
    </tr>
  );
  };

  return (
    <Transition
      show={isOpen}
      as={Fragment}
      enter="ease-out duration-300"
      enterFrom="opacity-0 scale-95"
      enterTo="opacity-100 scale-100"
      leave="ease-in duration-200"
      leaveFrom="opacity-100 scale-100"
      leaveTo="opacity-0 scale-95"
    >
      <div 
        className="fixed z-[99999999] pointer-events-auto"
        style={{ top: '50%', left: '60%', transform: 'translate(-50%, -50%)', width: '90%', maxWidth: '48rem' }}
      >
        <div className="relative transform overflow-hidden rounded-xl bg-white text-left shadow-2xl border border-gray-200">
          <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-blue-50 sm:mx-0 sm:h-10 sm:w-10">
                <Clock className="h-6 w-6 text-blue-600" aria-hidden="true" />
              </div>
              <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                <h3 className="text-lg font-semibold leading-6 text-gray-900 flex items-center gap-2">
                  Version History
                </h3>
                      <p className="text-sm text-gray-500 mt-1 flex items-center gap-1.5">
                        <LucideFileIcon size={14} />
                        {fileName}
                      </p>
                      
                      <div className="mt-4">
                        {pendingUploadFile ? (
                          <div className="p-4 rounded-lg border border-gray-200 bg-gray-50 flex flex-col gap-3">
                            <h4 className="text-sm font-semibold text-gray-900">Upload New Version</h4>
                            <p className="text-xs text-gray-500">File: {pendingUploadFile.name}</p>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Version Description</label>
                              <input 
                                type="text"
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-3 py-2 border"
                                value={uploadNote}
                                onChange={(e) => setUploadNote(e.target.value)}
                                autoFocus
                              />
                            </div>
                            <div className="flex justify-end gap-2 mt-2">
                              <button
                                type="button"
                                onClick={() => {
                                  setPendingUploadFile(null);
                                  if (fileInputRef.current) fileInputRef.current.value = "";
                                }}
                                className="rounded-md bg-white px-3 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                onClick={executeUpload}
                                disabled={loading}
                                className="rounded-md bg-primary-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:opacity-50"
                              >
                                {loading ? "Uploading..." : "Upload"}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <input type="file" className="hidden" ref={fileInputRef} onChange={handleFileSelected} />
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                className="inline-flex items-center gap-1.5 rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-primary-600 shadow-sm ring-1 ring-inset ring-primary-300 hover:bg-gray-50"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
                                </svg>
                                Upload New Version
                              </button>

                              {fileName.toLowerCase().endsWith(".svg") && (
                                <button
                                  type="button"
                                  onClick={async () => {
                                    try {
                                      const svgString = cadviewer.cvjs_extractSVGfromCanvas("floorPlan");
                                      if (!svgString) throw new Error("Could not extract SVG from canvas.");
                                      
                                      const blob = new Blob([svgString], { type: "image/svg+xml" });
                                      const file = new File([blob], fileName, { type: "image/svg+xml" });
                                      
                                      setPendingUploadFile(file);
                                      setUploadNote("v" + Math.max(2, versions.length + 1));
                                    } catch(e: any) {
                                      alert(e.message || "Failed to extract SVG");
                                    }
                                  }}
                                  className="inline-flex items-center gap-1.5 rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-green-600 shadow-sm ring-1 ring-inset ring-green-300 hover:bg-gray-50"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg>
                                  Save Canvas as New Version
                                </button>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                      
                      <div className="mt-6 w-full overflow-y-auto max-h-[60vh] rounded-lg border border-gray-200">
                        <table className="w-full text-left border-collapse">
                          <thead>
                            <tr className="bg-gray-50/50 border-b border-gray-200">
                              <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Version</th>
                              <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                              <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Size</th>
                              <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white">
                            {loading ? (
                              <tr>
                                <td colSpan={4} className="py-8 text-center text-sm text-gray-500">
                                  <div className="flex justify-center items-center gap-2">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                                    Loading versions...
                                  </div>
                                </td>
                              </tr>
                            ) : (
                                <>
                                  {versions.length > 0 ? (
                                    versions.map((v, index) => renderVersionRow(v, index === 0, currentVersion))
                                  ) : currentVersion ? (
                                    renderVersionRow(currentVersion, true, currentVersion)
                                  ) : null}
                                  {!currentVersion && versions.length === 0 && (
                                  <tr>
                                    <td colSpan={4} className="py-8 text-center text-sm text-gray-500">
                                      No version history found.
                                    </td>
                                  </tr>
                                )}
                              </>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
          <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6 border-t border-gray-100">
            <button
              type="button"
              className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </Transition>
  );
};
